import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const FeaturesCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Carousel slides data with titles, hooks, and descriptions
  const slides = [
    {
      id: 1,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/unu5y1wo_morning.JPG',
      title: 'Morning Zone Analysis',
      hook: 'Ξεκίνα τη μέρα με data',
      description: 'Αναλυτικά στατιστικά για τη ζώνη 06:00-14:00 με top tracks και μετρικές απόδοσης.'
    },
    {
      id: 2,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/b08c3q8t_drive.JPG',
      title: 'Drive Time Insights',
      hook: 'Peak hours, peak performance',
      description: 'Ανάλυση της ζώνης 14:01-18:00 - η πιο κρίσιμη ώρα για το ραδιόφωνο.'
    },
    {
      id: 3,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/k1tye7mf_night.JPG',
      title: 'Night Zone Stats',
      hook: 'Νυχτερινή ακροαματικότητα decoded',
      description: 'Στατιστικά για τη ζώνη 18:01-22:00 με πλήρη ανάλυση top τραγουδιών.'
    },
    {
      id: 4,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/hgoiw9ad_overnight.JPG',
      title: 'Overnight Performance',
      hook: 'Ακόμα και τη νύχτα, μετράμε',
      description: 'Ανάλυση της ζώνης 22:01-05:59 για ολοκληρωμένη εικόνα 24ώρου.'
    },
    {
      id: 5,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/1yukfb2g_playlist.JPG',
      title: 'Playlist Analyzer',
      hook: 'Αποφάσεις με βάση τα δεδομένα',
      description: 'Scoring τραγουδιών, κατηγοριοποίηση και recommendations για το playlist σου.'
    },
    {
      id: 6,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/xcaaxo1c_song%20analyzer.JPG',
      title: 'Song Performance Tracker',
      hook: 'Κάθε τραγούδι έχει ιστορία',
      description: 'Retention, Early Drop, LSI scores - όλα τα metrics που χρειάζεσαι για κάθε track.'
    },
    {
      id: 7,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/s5ieigrk_stream%20listeners%201.JPG',
      title: 'Hourly Listeners Analysis',
      hook: 'Ακροατές ανά ώρα & ημέρα',
      description: 'Λεπτομερής ανάλυση ακροαματικότητας με γραφήματα ανά ώρα και trends.'
    },
    {
      id: 8,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/mk7eath7_stream%20listeners%202.JPG',
      title: 'Daily Trends',
      hook: 'Η εβδομάδα σε αριθμούς',
      description: 'Ημερήσια ανάλυση ακροατών με σύγκριση και ποσοστιαίες μεταβολές.'
    },
    {
      id: 9,
      image: 'https://customer-assets.emergentagent.com/job_wave-analyzer-3/artifacts/dtukd806_stream%20listeners%203.JPG',
      title: 'Audience Heatmap',
      hook: 'Η θερμοκρασία της ακροαματικότητας',
      description: 'Χρωματική απεικόνιση ακροατών ανά ώρα και ημέρα εβδομάδας - spot the peaks!'
    }
  ];

  // Auto-advance every 3 seconds
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isPaused, slides.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  return (
    <div 
      className="relative w-full max-w-5xl mx-auto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Main carousel container */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#2D0C4B]/80 to-[#1A0730]/90 border border-purple-500/30 shadow-2xl">
        
        {/* Slide container - adjusted for full image visibility */}
        <div className="relative bg-[#1A0730] overflow-hidden" style={{ minHeight: '500px' }}>
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                index === currentIndex 
                  ? 'opacity-100 translate-x-0' 
                  : index < currentIndex 
                    ? 'opacity-0 -translate-x-full' 
                    : 'opacity-0 translate-x-full'
              }`}
            >
              {/* Image - contain to show full image without cropping */}
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-contain bg-[#1A0730]"
                style={{ maxHeight: '420px', margin: '0 auto', display: 'block' }}
              />
              
              {/* Gradient overlay - bottom */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A0730] via-transparent to-transparent opacity-90" />
              
              {/* Content overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                {/* Title badge */}
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D] rounded-full mb-3">
                  <span className="text-[#2D0C4B] font-bold text-sm">{slide.title}</span>
                </div>
                
                {/* Hook slogan */}
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2 drop-shadow-lg">
                  {slide.hook}
                </h3>
                
                {/* Description - shown on hover via CSS */}
                <p className="text-purple-200/90 text-sm sm:text-base max-w-2xl transition-all duration-300 opacity-0 group-hover:opacity-100 hover:opacity-100"
                   style={{ opacity: isPaused ? 1 : 0.7 }}>
                  {slide.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Navigation arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-3 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white transition-all duration-200 hover:scale-110 z-10"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        
        <button
          onClick={goToNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white transition-all duration-200 hover:scale-110 z-10"
          aria-label="Next slide"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        
        {/* Pause indicator */}
        {isPaused && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium">
            ⏸ Paused
          </div>
        )}
      </div>
      
      {/* Dots navigation */}
      <div className="flex justify-center gap-2 mt-4">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'bg-[#FF9F0A] w-8 shadow-lg shadow-[#FF9F0A]/50'
                : 'bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Slide counter */}
      <div className="text-center mt-3 text-purple-200/70 text-sm">
        {currentIndex + 1} / {slides.length}
      </div>
    </div>
  );
};

export default FeaturesCarousel;
